import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const CAL_LINK = "https://cal.com/kalza-studio-hdho6x";

const Highlight = ({ children }) => (
  <span className="relative inline-block font-accent">
    <span className="absolute left-0 right-0 bottom-[0.08em] h-[0.8em] bg-[#BE123C] -z-0" />
    <span className="relative z-10">{children}</span>
  </span>
);

export default function AboutPagePL() {
  return (
    <main data-testid="about-page-pl">
      {/* ═══ 1. HERO ═══ */}
      <section className="pt-32 pb-20 md:pb-28 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-[#D4A0A0] mb-6 block">JAK MYŚLĘ O BRANDINGU</span>
            <h1 className="mb-8">
              <span className="font-display text-5xl md:text-6xl lg:text-7xl text-white block leading-[0.95]">DLACZEGO PRAWDA</span>
              <span className="font-accent text-5xl md:text-6xl lg:text-7xl text-[#BE123C] block mt-2 leading-[1.1]">jest strategią</span>
            </h1>
            <p className="font-body text-base md:text-lg text-white/70 leading-relaxed max-w-xl">
              Większość porad o brandingu mówi Ci: dopracuj, zaprezentuj się, pozycjonuj strategicznie. I właśnie dlatego tyle marek brzmi, jakby pisała je ta sama osoba. Te, które przyciągają najlepszych klientów, to te, w których właściciel przestał grać i po prostu powiedział to, co naprawdę myśli.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══ 2. PRZEKONANIE ═══ */}
      <section className="py-20 md:py-28 bg-[#F5F0EB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="font-serif text-3xl md:text-4xl text-[#1C1210] leading-[1.15]">
                Znasz to uczucie, kiedy poznajesz kogoś, kto wydaje się interesujący, no ale <span className="font-accent text-[#BE123C]">coś w tym nie gra?</span>
              </h2>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.15 }} className="space-y-4 font-body text-base text-[#6B6560] leading-[1.7]">
              <p>Może ktoś mówi o sobie w sposób, który nie zgadza się z energią, którą od niego czujesz. Nie potrafisz wskazać co dokładnie jest nie tak, po prostu nie do końca w to wierzysz. Dokładnie to robi niedopasowana marka z Twoimi odbiorcami. Każdego dnia.</p>
              <p>Branding polega na tym, żeby to co na zewnątrz zgadzało się z tym co w środku. Kiedy tego dopasowania brakuje, ludzie to wyczuwają. Nie powiedzą Ci dlaczego wyszli z Twojej strony po dziewięciu sekundach. Po prostu wyszli.</p>
              <p>Rozwiązaniem nigdy nie jest więcej polerowania. Jest więcej szczerości. Najbardziej magnetyczna wersja Twojej marki to ta, w której ktoś czyta Twoją stronę, wysyła link do swojej najlepszej przyjaciółki i mówi <em>spodobał mi się jej vibe, chcę z nią pracować</em>.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Divider 1 */}
      <section className="relative py-14 md:py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/40" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-white leading-snug">
              Klienci kupują <Highlight>prawdę</Highlight>. Wyczuwają wyreżyserowaną markę po trzech zdaniach. No ale kiedy Twoja strona brzmi jakbyś naprawdę to napisała, siedząc przy biurku z kawą, zatrzymują się i zostają. I wracają z kartą <Highlight>gotową do płacenia</Highlight>
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══ 3. CZYM JEST TOŻSAMOŚĆ MARKI ═══ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-[#8B1A3A] mb-4 block">CZYM WŁAŚCIWIE JEST TOŻSAMOŚĆ MARKI</span>
            <h2 className="mb-10">
              <span className="font-display text-3xl md:text-4xl text-[#1C1210]">POMYŚL O TYM JAK O </span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C]">osobie</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16">
              <div>
                <h3 className="font-serif text-xl text-[#1C1210] mb-3">Tożsamość marki to osobowość</h3>
                <p className="font-body text-base text-[#6B6560] leading-relaxed">Kim jesteś jako biznes. Za czym stoisz, jak mówisz, w co wierzysz. Kiedy ktoś opisuje Cię znajomemu, to jest to co powie.</p>
              </div>
              <div>
                <h3 className="font-serif text-xl text-[#1C1210] mb-3">Identyfikacja wizualna to wygląd</h3>
                <p className="font-body text-base text-[#6B6560] leading-relaxed">Logo, kolory, fonty, to jak wygląda Twój Instagram kiedy ktoś wchodzi na niego po raz pierwszy. Ubranie, które Twoja marka zakłada każdego ranka. To co ludzie widzą zanim usłyszą od Ciebie choćby jedno słowo.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ 4. MOJA HISTORIA ═══ */}
      <section className="py-20 md:py-28 bg-[#F5F0EB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <div className="aspect-[4/5] rounded-3xl overflow-hidden">
                <img src="/images/ola-story.jpg" alt="Aleksandra Kałuża" className="w-full h-full object-cover object-top" />
              </div>
              <div className="mt-10">
                <h3 className="font-eyebrow text-[#8B1A3A] mb-4">POZA PRACĄ</h3>
                <p className="font-body text-sm text-[#6B6560] leading-[1.7]">
                  Kiedy zamykam laptopa, pewnie czytam książkę o ludzkim umyśle, medytuję, gotuję coś z całego serca albo planuję kolejny wyjazd do SPA. Moi znajomi nazywają mnie ekspertką od SPA. Traktuję ten tytuł trochę zbyt poważnie. Muzyka jest ogromną częścią mojego życia, tak samo jak Brazylia. Razem z partnerem prowadzimy stowarzyszenie, które sprowadza brazylijski footvolley i kulturę do Europy.
                </p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-4 block">MOJA HISTORIA</span>
              <h2 className="mb-8">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block">OD ZAWSZE</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block mt-1">patrzę głębiej</span>
              </h2>
              <div className="space-y-5 font-body text-base text-[#6B6560] leading-[1.7]">
                <p>To jak pracuję ukształtowały dwie rzeczy, które na pierwszy rzut oka nie mają ze sobą nic wspólnego, no ale w praktyce się nawzajem napędzają.</p>
                <div className="border-l-2 border-[#BE123C]/30 pl-6 my-6">
                  <span className="font-eyebrow text-[#8B1A3A] text-xs block mb-3">PIERWSZA: STRUKTURA</span>
                  <p>Spędziłam lata w globalnych firmach w Europie, prowadząc zespoły kreatywne 40+ osób i realizując kampanie za miliony euro. Taki rodzaj pracy, w której w poniedziałek prezentujesz zarządowi, a w piątek kampania leci na sześciu rynkach jednocześnie. To nauczyło mnie budować marki, które się trzymają kiedy na stole leżą prawdziwe pieniądze.</p>
                </div>
                <div className="border-l-2 border-[#BE123C]/30 pl-6 my-6">
                  <span className="font-eyebrow text-[#8B1A3A] text-xs block mb-3">DRUGA: OSOBISTA ZMIANA</span>
                  <p>A potem życie wkroczyło na scenę. Wypalenie, wypadek i sygnał od Wszechświata, którego nie dało się zignorować: ten rozdział się skończył. Nie powolne wygasanie. Jednego dnia życie, które zbudowałam, po prostu przestało pasować. Musiałam dowiedzieć się jak wygląda nowy rozdział. Co tak naprawdę mam teraz robić.</p>
                  <p className="mt-4">To szukanie wciągnęło mnie w psychologię, ludzkie zachowania, archetypy, pracę z tożsamością, duchowość. Weszłam w to na poważnie.</p>
                </div>
                <p>To co z tego wyszło to metodologia znajdowania własnej <strong className="text-[#1C1210]">esencji</strong>. Nauczyłam się rozpoznawać gdzie jest mój flow i odkrywać talenty, które już we mnie były, tylko niewidoczne na pełnych obrotach. Bo de facto już jesteśmy kompletni.</p>
                <p>Kiedy poznaję kogoś, kto prowadzi świetny biznes, a jego marka wygląda jak kolejny szablon z Canvy, to mnie wkurza. Widzę jego talent siedzący tuż obok i jednocześnie widzę każdego klienta, którego traci, bo nikt nie przetłumaczył tej marki porządnie. Więc to właśnie robię teraz. Pomagam zobaczyć kim są, co ich wyróżnia i tłumaczę to na markę, którą ludzie czują. Strategia i wizualia.</p>
                <p>A kiedy to klika, ich klienci przestają porównywać ceny i przestają prosić o referencje. Umawiają się na rozmowę, bo coś w tej marce dało im poczucie <em>to jest moja osoba</em>. I to uczucie zaczęło się w pierwszych trzech sekundach.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Divider 2 */}
      <section className="relative py-14 md:py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/40" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-white leading-snug">
              Im bardziej jesteś <Highlight>niefiltrowana</Highlight>, tym szybciej ludzie podejmują decyzję. Twoje dziwactwa, mocne opinie, ta jedna rzecz, którą boisz się powiedzieć na głos na swojej stronie. To jest dokładnie to co sprawia, że właściwy klient czyta Twoją stronę i myśli <Highlight>gdzie ona była cały ten czas</Highlight>
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══ 5. JAK PRACUJĘ ═══ */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-4 block">JAK PRACUJĘ</span>
              <h2 className="mb-6">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block">MIĘDZY WRAŻLIWOŚCIĄ</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block mt-1">a strukturą</span>
              </h2>
              <div className="space-y-4 font-body text-base text-[#6B6560] leading-[1.7]">
                <p>Zanim w ogóle się spotkamy na rozmowie, już przejrzałam Twoją stronę, Instagram, ofertę, sposób w jaki piszesz. Zanim usiądziemy, zazwyczaj już wiem gdzie jest rozjazd.</p>
                <p>Wtedy zaczynają się niewygodne pytania. Te, których Twoi znajomi Ci nie zadadzą, bo są zbyt grzeczni. Na przykład dlaczego najciekawsza część tego co robisz jest zakopana trzy kliknięcia w głąb Twojej strony.</p>
                <p>Dla osób, które jeszcze nie wiedzą kim są w kontekście swojej marki, prowadzę pogłębiony proces tożsamościowy, który budowałam latami. Wspólnie znajdujemy co Cię wyróżnia i zamieniamy to w coś, co Twoi odbiorcy wyłapują w sekundach.</p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="lg:mt-16">
              <div className="bg-[#F5F0EB] rounded-2xl p-8">
                <h3 className="font-eyebrow text-[#8B1A3A] mb-6">CO CENIĘ</h3>
                <ul className="space-y-4">
                  {[
                    { icon: "\u2600\uFE0E", text: "Prawda ponad granie" },
                    { icon: "\u2658", text: "Odwaga jako inicjacja" },
                    { icon: "\u2665\uFE0E", text: "Głębia emocjonalna ponad powierzchnię" },
                    { icon: "\u2666\uFE0E", text: "Intuicja na pierwszym miejscu" },
                    { icon: "\u2655", text: "Jakość ponad ilość" },
                    { icon: "\u2667", text: "Prawdziwe partnerstwo" },
                    { icon: "\u2650\uFE0E", text: "Szacunek do energii" },
                  ].map((value, i) => (
                    <li key={i} className="font-serif text-lg text-[#1C1210] flex items-center gap-4">
                      <span className="text-[#BE123C] text-xl w-7 h-7 flex items-center justify-center flex-shrink-0">{value.icon}</span>
                      {value.text}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 6. Z KIM PRACUJĘ ═══ */}
      <section className="py-20 md:py-28 bg-[#F5F0EB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-12 max-w-3xl">
            <span className="font-eyebrow text-[#8B1A3A] mb-4 block">Z KIM PRACUJĘ</span>
            <h2 className="mb-6">
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block">WŁAŚCICIELE BIZNESÓW</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block mt-1">gotowi na jasność</span>
            </h2>
            <p className="font-body text-base text-[#6B6560] leading-[1.7]">
              Osoby prowadzące własne biznesy i małe zespoły, których praca jest już dobra, no ale marka za nią nie nadąża. Mają dość brzmieniu generycznie i są gotowe na markę, która udźwignie to co przed nimi.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: "Na starcie", body: "Wiesz co robisz i robisz to dobrze. No ale kiedy trzeba to pokazać w sieci, słowa brzmią jak u wszystkich w branży. Marka jeszcze nie brzmi jak Ty.", img: "/images/mood/arch-sea.jpg" },
              { title: "W transformacji", body: "Zmieniłaś się, masz inny kierunek niż dwa lata temu. A marka wciąż opowiada starą historię. Budujesz teraz z innego miejsca i Twoja komunikacja powinna to pokazywać.", img: "/images/mood/water-light.jpg" },
              { title: "Z doświadczeniem", body: "Biznes ma trakcję. Klienci wracają, polecają Cię i ufają Twojej pracy. No ale Twoja strona i Instagram nie pokazują wartości, którą faktycznie dajesz. Marka nie nadąża.", img: "/images/mood/wood-amber.jpg" },
            ].map((card, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }} className="overflow-hidden">
                <div className="h-52 md:h-56 overflow-hidden rounded-t-2xl">
                  <img src={card.img} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="pt-6 pb-2">
                  <h3 className="font-serif text-lg text-[#1C1210] mb-2">{card.title}</h3>
                  <p className="font-body text-sm text-[#6B6560] leading-relaxed">{card.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 7. CTA ═══ */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/arch-sea.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="bg-[#1C1210]/90 backdrop-blur-sm px-10 py-14 md:px-16 md:py-20 rounded-2xl text-center">
              <h2 className="mb-4">
                <span className="font-serif text-3xl md:text-4xl text-white block">Gotowa na markę, która</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block mt-2">w końcu brzmi jak Ty?</span>
              </h2>
              <p className="font-body text-base text-white/60 leading-relaxed mb-10 max-w-xl mx-auto">
                Jeśli Twój biznes jest lepszy niż to, jak wygląda w sieci, powinnyśmy porozmawiać. Umów rozmowę, a powiem Ci dokładnie co widzę.
              </p>
              <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white font-body font-medium text-xs tracking-[2px] uppercase px-10 py-5 hover:bg-[#BE123C] transition-colors duration-300">
                UMÓW ROZMOWĘ <ArrowUpRight size={16} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
